import { ArrowUturnLeftIcon, ArrowUturnRightIcon } from '@heroicons/react/24/outline';
import EyeSlashIcon from '@heroicons/react/24/outline/EyeSlashIcon';
import debounce from 'lodash/debounce';
import { useContext, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { ButtonPanel } from '@/components/core/ButtonPanel/ButtonPanel';
import { DialogHeader } from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { SearchInput } from '@/components/core/SearchInput/SearchInput';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { isHiddenAttribute } from '@/components/datamodel/hooks/useIsHiddenAttribute';
import { FocusAttributeContext } from '@/dialogs/item-editor/Context/FocusAttributeContext.ts';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { useUndoRedo } from '@/hooks/useUndoRedo';
import { useUser } from '@/hooks/useUser';
import { DataModelAttribute, PersistedItem } from '@/types/DataModel';

import { useSections } from '../../../components/datamodel/hooks/useSections';
import { useSecondaryItemTitle } from '../hooks/useSecondaryItemTitle';
import { ItemComplianceIndicator } from './ItemComplianceIndicator';
import { ItemDialogHeaderSecondary } from './ItemDialogHeaderSecondary';
import { ItemEditorSettings } from './ItemEditorSettings';
import { ItemEditorTitle } from './ItemEditorTitle';
import { NavigationDropdownMenu } from './navigation-dropdown-menu/NavigationDropdownMenu';

interface ItemEditorDialogHeaderProps {
    category?: string;
    isCreatingNewItem: boolean;
    persistedItem: PersistedItem | undefined;
    onNavigateToItem?: (primaryKey: string) => void;
}

interface Attribute extends DataModelAttribute {
    section?: string | undefined;
}

interface LayoutSection {
    name: string;
    attributes: DataModelAttribute[];
}

interface MatchedAttribute extends DataModelAttribute {
    section: string;
    translatedLabel?: string;
}

const AttributeResultRow = ({ attribute }: { attribute: MatchedAttribute }) => {
    const hidden = isHiddenAttribute({ attribute });
    return (
        <div className="tw-flex tw-justify-start tw-items-center font-dynamic">
            {hidden && (
                <Icon
                    icon={EyeSlashIcon}
                    size="sm"
                    variant="lighter-gray"
                    className="tw-mr-2"
                />
            )}
            {attribute.translatedLabel || attribute.label}
        </div>
    );
};

const UndoRedoButtons = ({
    undo,
    redo,
    canUndo,
    canRedo,
}: {
    undo: (e?: MouseEvent) => void;
    redo: (e?: MouseEvent) => void;
    canUndo: boolean;
    canRedo: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <>
            <ButtonPanel
                icon={ArrowUturnLeftIcon}
                isDisabled={!canUndo}
                tooltip={t('UNDO')}
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => undo(e.nativeEvent)}
                data-testid="undo-button"
            />
            <ButtonPanel
                icon={ArrowUturnRightIcon}
                isDisabled={!canRedo}
                tooltip={t('REDO')}
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => redo(e.nativeEvent)}
                data-testid="redo-button"
            />
        </>
    );
};

const HeaderSearchInput = ({
    searchTerm,
    filteredAttributes,
    onSearchTermChange,
    onClear,
    onResultClick,
    placeholder,
}: {
    searchTerm: string;
    filteredAttributes: MatchedAttribute[];
    onSearchTermChange: (value: string) => void;
    onClear: () => void;
    onResultClick: (attr: DataModelAttribute) => void;
    placeholder: string;
}) => (
    <SearchInput<MatchedAttribute>
        id="editor-attribute-search-input"
        data-testid="search-input"
        placeholder={placeholder}
        searchResults={filteredAttributes}
        value={searchTerm}
        onChange={onSearchTermChange}
        onClear={onClear}
        onResultClick={onResultClick}
        getResultKey={(attribute) => attribute.name}
        isResultDisabled={(attribute) => isHiddenAttribute({ attribute })}
        getResultAttributes={(attribute) => ({
            'data-attribute-name': attribute.name,
            'data-attribute-label': attribute.translatedLabel || attribute.label,
            'data-hidden': isHiddenAttribute({ attribute }) ? 'true' : 'false',
        })}
        renderResult={(attribute) => <AttributeResultRow attribute={attribute} />}
        containerClassName="tw-static"
    />
);

const HeaderActions = ({
    searchInputProps,
    undoRedo,
}: {
    searchInputProps: {
        searchTerm: string;
        filteredAttributes: MatchedAttribute[];
        onSearchTermChange: (value: string) => void;
        onClear: () => void;
        onResultClick: (attr: DataModelAttribute) => void;
        placeholder: string;
    };
    undoRedo: {
        undo: (e?: MouseEvent) => void;
        redo: (e?: MouseEvent) => void;
        canUndo: boolean;
        canRedo: boolean;
    };
}) => (
    <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
        <UndoRedoButtons {...undoRedo} />
        <HeaderSearchInput {...searchInputProps} />
        <ItemEditorSettings />
    </div>
);

const ItemEditorDialogHeader = ({
    category,
    isCreatingNewItem,
    persistedItem,
    onNavigateToItem,
}: ItemEditorDialogHeaderProps) => {
    const { user } = useUser();
    const { organization } = useOrganization();
    const SearchTermRef = useRef('');
    const { t } = useTranslation();
    const focusContext = useContext(FocusAttributeContext);
    const undoRedo = useUndoRedo();

    const selectedCategory = useWatch({
        name: 'category__',
        defaultValue: category,
    });

    const layoutSections = useSections(selectedCategory);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredAttributes, setFilteredAttributes] = useState<MatchedAttribute[]>([]);
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { hasContent: hasSecondaryTitle } = useSecondaryItemTitle({ item: persistedItem });

    const handleSearch = debounce((term: string, { layoutSections }) => {
        if (!term) {
            setFilteredAttributes([]);
            return;
        }
        const matchedAttributes: MatchedAttribute[] = [];
        const seen = new Set<string>();
        const searchTermLower = term.toLowerCase();
        layoutSections &&
            layoutSections.forEach((section: LayoutSection) => {
                section.attributes.forEach((attr: Attribute) => {
                    if (seen.has(attr.name)) {
                        return;
                    }
                    const translatedLabel = translateAttributeLabel(attr);
                    const label = attr.label || '';

                    if (
                        (label && label.toLowerCase().includes(searchTermLower)) ||
                        (translatedLabel && translatedLabel.toLowerCase().includes(searchTermLower))
                    ) {
                        seen.add(attr.name);
                        matchedAttributes.push({
                            ...attr,
                            section: section.name,
                            translatedLabel: translatedLabel,
                        });
                    }
                });
            });
        setFilteredAttributes(matchedAttributes);
    }, 200);

    useEffect(() => {
        handleSearch(searchTerm.trim(), layoutSections);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    if (!focusContext) {
        return;
    }
    const { setFocusAttribute, setPinnedAttributeName } = focusContext;

    const handleSearchedAttributeClicked = (attr: DataModelAttribute) => {
        if (isHiddenAttribute({ attribute: attr })) return;

        setSearchTerm('');
        SearchTermRef.current = '';
        setFilteredAttributes([]);

        setPinnedAttributeName(attr.name);
        setFocusAttribute(attr);

        setTimeout(() => {
            setFocusAttribute(undefined);
            requestAnimationFrame(() => {
                setFocusAttribute(attr);
            });
        }, 150);
    };

    const handleSearchBoxClear = () => {
        setFilteredAttributes([]);
        setSearchTerm('');
    };

    const showSecondaryBar = !isCreatingNewItem && hasSecondaryTitle;

    if (!user || !organization) {
        return;
    }

    const searchInputProps = {
        searchTerm,
        filteredAttributes,
        onSearchTermChange: setSearchTerm,
        onClear: handleSearchBoxClear,
        onResultClick: handleSearchedAttributeClicked,
        placeholder: t('EDITOR.ATTRIBUTE_SEARCH_LABEL'),
    };

    return (
        <div className="tw-flex tw-flex-col">
            <DialogHeader>
                <div className="tw-relative tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-1 md:tw-flex-row md:tw-items-center md:tw-justify-between">
                    <div className="tw-flex tw-w-full tw-items-center tw-justify-between md:tw-hidden">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <UndoRedoButtons {...undoRedo} />
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <HeaderSearchInput {...searchInputProps} />
                            <ItemEditorSettings />
                        </div>
                    </div>

                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-content-center tw-items-center tw-gap-x-2 tw-gap-y-3 md:tw-flex-nowrap md:tw-gap-y-0 lg:tw-gap-x-3">
                        <ItemEditorTitle item={persistedItem} isCreatingNewItem={isCreatingNewItem} />
                        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1 lg:tw-gap-2">
                            <NavigationDropdownMenu
                                isCreatingNewItem={isCreatingNewItem}
                                item={persistedItem}
                                onNavigateToItem={onNavigateToItem}
                            />
                            <ItemComplianceIndicator />
                        </div>
                    </div>

                    <div className="tw-hidden md:tw-flex">
                        <HeaderActions searchInputProps={searchInputProps} undoRedo={undoRedo} />
                    </div>
                </div>
            </DialogHeader>
            {showSecondaryBar && <ItemDialogHeaderSecondary item={persistedItem} />}
        </div>
    );
};

ItemEditorDialogHeader.DisplayName = 'ItemEditorDialogHeader';

export { ItemEditorDialogHeader };
